import React, {useContext, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import cx from 'classnames';
import {BackgroundContainer} from '~/common/fabric/CreateDesign/components/Background/BackgroundContainer';
import {StampContainer} from '~/common/fabric/CreateDesign/components/Stamp/StampContainer';
import {Sections} from '~/common/fabric/CreateDesign/constants';
import {CardEditorMobile} from '~/common/fabric/CreateDesign/components/CardEditor/Mobile/CardEditorMobile';
import {BackgroundEditorMobile} from '~/common/fabric/CreateDesign/components/Background/Mobile/BackgroundEditorMobile';
import {EnvelopeEditorMobile} from '~/common/fabric/CreateDesign/components/Envelope/Mobile/EnvelopeEditorMobile';
import {StampEditorMobile} from '~/common/fabric/CreateDesign/components/Stamp/Mobile/StampEditorMobile';
import {
  selectBackgroundColor,
  selectCurrentSection,
  selectEditingMobile,
  selectPremiumFeaturesWarning,
} from '~/CreateFabricGreetingCard/reducers/fabricDesign/selectors';
import {UPDATE_DESIGN_OPTIONS} from '~/CreateFabricGreetingCard/reducers/fabricDesign/constants';
import {useBackground} from '~/common/fabric/CreateDesign/components/Background/actions/useBackground';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {EnvelopeContainer} from '~/common/fabric/CreateDesign/components/Envelope/EnvelopeContainer';
import {NavBottom} from '~/common/fabric/CreateDesign/components/NavBottom/NavBottom';
import {EventCreateStampParent} from '~/common/fabric/CreateDesign/components/Stamp/EventCreateStampContext';
import {GreetingCardTemplate} from '~/CreateFabricGreetingCard/routes/CardCreateDesign/GreetingCardTemplate';
import {FabricContext, FabricContextParent} from '~/common/fabric/context/FabricContext';
import {Video} from '~library/atoms/Video';
import {
  selectPristineBackground,
  selectShouldTogglePremiumBackground,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {selectIsPremium} from '~/CreateFabricInvitation/reducers/fabricTemplate/selectors';
import {silver} from '~libSass/base/_exports.sass';
import './CardCreateDesign.sass';

const sectionComponentsMap = {
  [Sections.CARD_DESIGN]: GreetingCardTemplate,
  [Sections.BACKGROUND]: BackgroundContainer,
  [Sections.ENVELOPE_LINER]: EnvelopeContainer,
  [Sections.STAMP]: StampContainer,
};

const sectionEditMobileMap = {
  [Sections.CARD_DESIGN]: CardEditorMobile,
  [Sections.BACKGROUND]: BackgroundEditorMobile,
  [Sections.ENVELOPE_LINER]: EnvelopeEditorMobile,
  [Sections.STAMP]: StampEditorMobile,
};

// Prevents react from re-rendering the video bg when moving from one asset editor to another
const MemoizedVideo = React.memo(Video);

const CardCreateDesign = () => {
  const {canvas} = useContext(FabricContext);
  const editingMobile = useSelector(selectEditingMobile);
  const currentSection = useSelector(selectCurrentSection);
  const backgroundColor = useSelector(selectBackgroundColor);
  const pristineBackground = useSelector(selectPristineBackground);
  const shouldTogglePremiumBackground = useSelector(selectShouldTogglePremiumBackground);
  const isPremium = useSelector(selectIsPremium);
  const premiumFeaturesWarning = useSelector(selectPremiumFeaturesWarning);
  const Section = sectionComponentsMap[currentSection];
  const NavBottomMobile = sectionEditMobileMap[currentSection];
  const isDesktop = useIsLargishAndUp();
  const isMobile = !isDesktop;
  const {selectedBackground} = useBackground(isMobile);
  const dispatch = useDispatch();

  useEffect(() => {
    canvas?.discardActiveObject()?.requestRenderAll();
  }, [canvas, isMobile]);

  useEffect(() => {
    window.dataLayer.push({event_is_draft: true});
  }, []);

  useEffect(() => {
    const onSelectionEvent = (value) => {
      if (isMobile) {
        dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload: {editingMobile: value},
        });
      }
    };
    const handleSelectionCreatedOrUpdated = () => onSelectionEvent(true);
    const handleSelectionCleared = () => onSelectionEvent(false);

    canvas?.on('selection:created', handleSelectionCreatedOrUpdated);
    canvas?.on('selection:updated', handleSelectionCreatedOrUpdated);
    canvas?.on('selection:cleared', handleSelectionCleared);

    return () => {
      canvas?.off('selection:created', handleSelectionCreatedOrUpdated);
      canvas?.off('selection:updated', handleSelectionCreatedOrUpdated);
      canvas?.off('selection:cleared', handleSelectionCleared);
    };
  }, [canvas, isMobile, dispatch]);

  useEffect(() => {
    // NOTE: this starts the process of rendering gallery uploaded img on canvas in step 1
    const searchParams = new URLSearchParams(window.location.search);
    if (
      searchParams.has('previous') &&
      (['design_your_own_cards', 'all_design_your_own_cards'].includes(
        searchParams.get('previous')
      ) ||
        ['upload+your+own', 'upload your own', 'design+your+own', 'design your own'].some((t) =>
          searchParams.getAll('invitation_type').includes(t)
        ) ||
        searchParams.get('previous').search(/dyo_uyo/) !== -1)
    ) {
      dispatch({type: UPDATE_DESIGN_OPTIONS, payload: {shouldUseCachedGalleryPhoto: true}});
    }
  }, [dispatch]);

  // Set background css
  const style = {};
  const backgroundIsImage = selectedBackground?.src && !selectedBackground?.isVideo;
  if (isPremium || !pristineBackground) {
    if (backgroundIsImage) {
      style.backgroundColor = backgroundColor;
      style.backgroundImage = `url(${selectedBackground?.src})`;
      style.backgroundSize = 'cover';
    } else {
      style.backgroundColor = backgroundColor;
    }
  } else if (shouldTogglePremiumBackground || !pristineBackground) {
    style.backgroundColor = backgroundColor;
  } else {
    style.backgroundColor = silver;
  }

  const dismissPremiumFeaturesWarning = () => {
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {
        premiumFeaturesWarning: false,
        ignorePremiumWarning: true,
      },
    });
  };
  return (
    <div className="event-create-design" style={style}>
      <div
        aria-label="Premium Features"
        role="button"
        tabIndex={-1}
        onKeyUp={null}
        className={cx('overlay', {open: premiumFeaturesWarning})}
        onClick={dismissPremiumFeaturesWarning}
        data-qa-id="premium-features-warning-overlay"
        style={{
          zIndex: 1,
        }}
      />
      <div className="event-create-design__content">
        {selectedBackground?.isVideo && (
          <MemoizedVideo
            src={selectedBackground.src}
            reload
            className="event-create-design__background"
          />
        )}
        <Section />
      </div>
      <div className="event-create-design__bottom event-create-design__bottom--ecards">
        {isMobile && <NavBottomMobile />}
        {(!editingMobile || isDesktop) && <NavBottom />}
      </div>
    </div>
  );
};

export const CardCreateDesignContainer = () => (
  <FabricContextParent>
    <EventCreateStampParent>
      <CardCreateDesign />
    </EventCreateStampParent>
  </FabricContextParent>
);
